import { useContext, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { TextField, Button, Stack } from "@mui/material";
import APP_CONST from "../../core/constants/app-constants";
import PasswordLessAuthService from "../../core/service/passwordless-service";
import { useNavigate } from "react-router";
import React from "react";
import DHBackdrop from "../generic/dh-backdrop";
import AuthLayout from "../generic/auth-layout";
import AppContext, { AppContextType } from "../../core/context/auth-context";
import { IAppDetails } from "../../core/interface/general/IGeneral";

const PasswordlessInitiation = () => {
    let navigate = useNavigate();
    const passwordLessAuthService = new PasswordLessAuthService();
    const { setAppContext } = useContext(AppContext) as AppContextType;
    const [loader, setLoader] = useState<boolean>(false);
    const defaultData = {
        username: ""
    };
    const {
        handleSubmit,
        control,
    } = useForm({
        defaultValues: defaultData,
    });

    // Today's OTP flow (email -> SMS), used as the passkey fallback and for admins without a passkey.
    const startOtpFlow = async (email: string) => {
        try {
            const data = await passwordLessAuthService.signIn(email);
            navigate("/email-verify", { state: { ...data, ...{ 'userName': email } } });
        } catch (error) {
        }
    };

    const onSubmit = async (loginData: any) => {
        setLoader(true);
        const email = loginData.username;
        try {
            // Passkey-first (decision 5). Ask Cognito which first-auth factors this admin has: WEB_AUTHN is
            // present only if they've enrolled a passkey. If so, run the passkey ceremony; otherwise fall
            // through to the exact OTP flow admins use today — no passkey prompt for non-enrolled admins.
            const started: any = await passwordLessAuthService.beginUserAuth(email);
            const availableChallenges: string[] = started?.nextStep?.availableChallenges ?? [];
            if (availableChallenges.includes('WEB_AUTHN')) {
                const passkeyResult: any = await passwordLessAuthService.continueWithPasskey();
                if (passkeyResult?.isSignedIn) {
                    const tokens = await passwordLessAuthService.getTokens();
                    sessionStorage.clear();
                    sessionStorage.setItem('token', tokens.idToken!);
                    // Record the sign-in factor so the token exchange can stamp loginMethod=passkey on the
                    // admin JWT. sessionStorage.clear() above (and in the OTP path) prevents stale values.
                    sessionStorage.setItem('loginMethod', 'passkey');
                    setAppContext((prevState: IAppDetails) => ({
                        ...prevState,
                        ...{ isLoggedIn: true, authToken: tokens.idToken, refreshToken: tokens.idToken }
                    }));
                    navigate("/console");
                    return;
                }
            }
            // No passkey for this admin (or it didn't complete) -> today's OTP flow.
            await startOtpFlow(email);
        } catch (error) {
            // Any USER_AUTH / passkey failure (cancel, no credential, unavailable) -> OTP fallback (no regression).
            await startOtpFlow(email);
        } finally {
            setLoader(false);
        }
    };

    return (
        <React.Fragment>
            {loader && <DHBackdrop></DHBackdrop>}
            <AuthLayout
                title="Sign in"
                subtitle="Enter your email and we'll send you a verification code."
            >
                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <Stack spacing={2.5}>
                        <Controller
                            name="username"
                            control={control}
                            rules={{
                                required: "This is required",
                                pattern: {
                                    value: APP_CONST.REGEX.EMAIL,
                                    message: "Enter a valid email address",
                                },
                            }}
                            render={({
                                field: { ref, ...field },
                                fieldState: { invalid, error },
                            }) => (
                                <TextField
                                    fullWidth
                                    autoFocus
                                    required
                                    label="Email address"
                                    {...field}
                                    inputRef={ref}
                                    error={invalid}
                                    helperText={error?.message}
                                />
                            )}
                        />
                        <Button type="submit" variant="contained" size="large" fullWidth>
                            Continue
                        </Button>
                    </Stack>
                </form>
            </AuthLayout>
        </React.Fragment>
    );
}
export default PasswordlessInitiation;
